import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Loader2, KeyRound, ArrowLeft, AlertTriangle } from 'lucide-react';

const MIN_PASSWORD_LENGTH = 8;

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // verifying → checking token_hash on mount
  // ready     → token verified, show the password form
  // error     → token invalid/expired, show fallback
  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type');

    if (!tokenHash || type !== 'recovery') {
      setStatus('error');
      setErrorMessage(
        'This reset link is missing required information. Please request a new one.'
      );
      return;
    }

    let cancelled = false;

    (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      });

      if (cancelled) return;

      if (error) {
        setStatus('error');
        setErrorMessage(
          error.message ||
            'This reset link is invalid or has expired. Please request a new one.'
        );
        return;
      }

      setStatus('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setSubmitting(false);
      setFormError(error.message || 'Could not update password. Please try again.');
      return;
    }

    // Sign out so the user lands on /login as a fresh visitor and must
    // explicitly sign in with their new password.
    await supabase.auth.signOut();

    toast.success('Password updated. Please sign in with your new password.');
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-4">
      <Card className="w-full max-w-md bg-white/5 border-white/10 backdrop-blur-lg">
        <CardHeader className="text-center">
          <div className="font-mono text-[#00d4aa] text-sm uppercase tracking-wider mb-2">
            EPSILLON MEDIA
          </div>
          <CardTitle className="text-3xl font-bold text-white">
            {status === 'error' ? 'Link invalid' : 'Set a new password'}
          </CardTitle>
          <CardDescription className="text-white/60">
            {status === 'error'
              ? 'Your reset link could not be verified.'
              : 'Choose a strong password you haven’t used before.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'verifying' && (
            <div className="flex items-center justify-center py-8 text-white/70">
              <Loader2 className="w-6 h-6 mr-2 animate-spin text-[#00d4aa]" />
              Verifying your reset link...
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div
                role="alert"
                className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-white/90 flex items-start"
              >
                <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 text-red-400 shrink-0" />
                <span>{errorMessage}</span>
              </div>
              <div className="text-center text-sm text-white/60">
                <Link
                  to="/forgot-password"
                  className="text-[#00d4aa] hover:underline inline-flex items-center"
                >
                  <ArrowLeft className="w-3 h-3 mr-1" />
                  Request a new reset link
                </Link>
              </div>
            </div>
          )}

          {status === 'ready' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-white/80">
                  New password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-white/80">
                  Confirm new password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="••••••••"
                />
              </div>

              {formError && (
                <div
                  role="alert"
                  className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                >
                  {formError}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Update password
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPassword;
